/**
 * Färgpaletter. Ljusläget (Ljust/Mörkt/System, theme.ts) och paletten är två separata val per
 * installation. Själva färgerna finns i styles.css (semantiska tokens per palett och läge); här finns det
 * som JavaScript behöver: namn, prover för väljaren, theme-color och händelseytor till FullCalendar (som
 * bara godtar vanliga färgvärden). test/palettes.test.ts kontrollerar att värdena stämmer med styles.css.
 */
export type PaletteId = 'dawn' | 'aurora' | 'original';
export const PALETTE_KEY = 'autoday.palette';
export const PALETTE_IDS: readonly PaletteId[] = ['dawn', 'aurora', 'original'];

export function isPaletteId(v: unknown): v is PaletteId {
  return v === 'dawn' || v === 'aurora' || v === 'original';
}

interface ModeColors {
  /** Prov i väljaren: bakgrund, yta, text, accent, stödfärg. */
  swatch: { bg: string; panel: string; text: string; accent: string; support: string };
  /** Webbläsarens/systemets fält (meta theme-color). */
  themeColor: string;
  /** Händelseyta i mörkt läge och neutral kant i ljust (FullCalendar). */
  raised: string;
  text: string;
  borderStrong: string;
}

export interface Palette {
  id: PaletteId;
  name: { sv: string; en: string };
  description: { sv: string; en: string };
  light: ModeColors;
  dark: ModeColors;
}

export const PALETTES: Record<PaletteId, Palette> = {
  dawn: {
    id: 'dawn',
    name: { sv: 'Gryning', en: 'Dawn' },
    description: { sv: 'Varm elfenben, djup bläckton och aprikos med iris som stöd.', en: 'Warm ivory, deep ink and apricot, with iris as support.' },
    light: { swatch: { bg: '#f8f3ea', panel: '#fffcf7', text: '#251c14', accent: '#a5441c', support: '#5a48b8' }, themeColor: '#fffcf7', raised: '#f1e8da', text: '#251c14', borderStrong: '#8b7a66' },
    dark: { swatch: { bg: '#11121c', panel: '#181a27', text: '#f1ebe1', accent: '#f4a674', support: '#b3a8ff' }, themeColor: '#11121c', raised: '#212436', text: '#f1ebe1', borderStrong: '#6c7192' },
  },
  aurora: {
    id: 'aurora',
    name: { sv: 'Norrsken', en: 'Aurora' },
    description: { sv: 'Djupt plommon och skog, klar jade och lite lavendel.', en: 'Deep plum and forest, clear jade and a touch of lavender.' },
    light: { swatch: { bg: '#f5f2f8', panel: '#fdfbfe', text: '#1d1626', accent: '#0b7553', support: '#6a44b4' }, themeColor: '#fdfbfe', raised: '#ece5f1', text: '#1d1626', borderStrong: '#7b6e89' },
    dark: { swatch: { bg: '#120e17', panel: '#1a1521', text: '#f2edf7', accent: '#3fd7a0', support: '#cdb6ff' }, themeColor: '#120e17', raised: '#241d2d', text: '#f2edf7', borderStrong: '#766889' },
  },
  original: {
    id: 'original',
    name: { sv: 'Porslin', en: 'Porcelain' },
    description: { sv: 'Varmt porslin, djup skog och mjuka limeaccenter.', en: 'Warm porcelain, deep forest and soft lime accents.' },
    light: { swatch: { bg: '#f6f5ef', panel: '#fffdf8', text: '#253d33', accent: '#243c32', support: '#2455b0' }, themeColor: '#fffdf8', raised: '#e9edde', text: '#253d33', borderStrong: '#74816c' },
    dark: { swatch: { bg: '#171c19', panel: '#222b24', text: '#edf0e7', accent: '#d7efb1', support: '#8ab4f8' }, themeColor: '#171c19', raised: '#2c382d', text: '#edf0e7', borderStrong: '#87947f' },
  },
};
